// Generate a printable per-room installer checklist for onsite crews
import { splitRoomLabel, UNASSIGNED_ROOM, groupCartByRoom } from './cartGroups';

const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const label = (v) => v ? String(v).replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—';

const extractInstallFields = (item) => {
  const config = item.config || item;
  const roomLabel = config.room_label || item.room_label || '';
  const { room, window: win } = splitRoomLabel(roomLabel);
  return {
    room: room || UNASSIGNED_ROOM,
    window: win || '—',
    width: config.width || '—',
    height: config.height || '—',
    mount: label(config.mount_type),
    controlSide: label(config.control_position),
    controlType: label(config.control_type),
    rollDirection: label(config.roll_direction),
    bracketType: label(config.bracket_type),
    bracketColor: label(config.bracket_color),
    motorType: config.motor_type ? label(config.motor_type) : null,
    fabric: config.fabric_color_name || config.fabric_color || '—',
    product: label(config.product_type || 'roller-shades'),
    qty: item.quantity || config.quantity || 1,
  };
};

export const openInstallSheet = (doc, docType = 'Quote') => {
  const items = doc.items || [];
  const groups = groupCartByRoom(items);
  const ref = doc.id?.slice(0, 8).toUpperCase() || 'N/A';
  const customer = doc.customer_name || '';
  const date = new Date(doc.created_at || Date.now()).toLocaleDateString();
  const address = [doc.billing_address, doc.billing_city, doc.billing_state, doc.billing_zip].filter(Boolean).join(', ') ||
                  [doc.shipping_address, doc.shipping_city, doc.shipping_state, doc.shipping_zip].filter(Boolean).join(', ') || '';

  let roomsHtml = '';
  groups.forEach((g) => {
    const rows = g.items.map((item) => {
      const f = extractInstallFields(item);
      return `<tr>
        <td class="win">${esc(f.window)}</td>
        <td class="size">${f.width}" x ${f.height}"</td>
        <td>${esc(f.mount)}</td>
        <td>${esc(f.controlSide)}</td>
        <td>${esc(f.controlType)}${f.motorType ? `<br><span class="sub">${esc(f.motorType)}</span>` : ''}</td>
        <td>${esc(f.rollDirection)}</td>
        <td>${esc(f.bracketColor)}</td>
        <td class="fabric">${esc(f.fabric)}</td>
        <td class="qty">${f.qty}</td>
        <td class="check"><span class="box"></span></td>
        <td class="notes"></td>
      </tr>`;
    });

    roomsHtml += `
      <div class="room-block">
        <div class="room-header">${esc(g.room)} <span class="shade-count">${g.quantity} ${g.quantity === 1 ? 'shade' : 'shades'}</span></div>
        <table>
          <thead><tr>
            <th>Window</th>
            <th>Size (W x H)</th>
            <th>Mount</th>
            <th>Control Side</th>
            <th>Control Type</th>
            <th>Roll</th>
            <th>Bracket</th>
            <th>Fabric / Color</th>
            <th>Qty</th>
            <th class="check-h">Done</th>
            <th class="notes-h">Notes</th>
          </tr></thead>
          <tbody>${rows.join('')}</tbody>
        </table>
      </div>`;
  });

  const html = `<!DOCTYPE html><html><head>
    <title>Install Sheet — ${esc(ref)}</title>
    <style>
      @page { size: landscape; margin: 0.4in; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #171717; padding: 16px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #171717; padding-bottom: 10px; margin-bottom: 14px; }
      .header h1 { font-size: 20px; letter-spacing: 1px; }
      .header .meta { text-align: right; font-size: 11px; line-height: 1.6; }
      .header .meta strong { display: inline-block; min-width: 70px; }
      .room-block { margin-bottom: 18px; break-inside: avoid; }
      .room-header { font-size: 14px; font-weight: 700; background: #f5f5f4; padding: 6px 10px; border-left: 4px solid #d97706; margin-bottom: 2px; }
      .shade-count { font-weight: 400; color: #888; font-size: 11px; margin-left: 6px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #fafaf9; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #555; padding: 5px 6px; border-bottom: 2px solid #d6d3d1; text-align: left; white-space: nowrap; }
      td { padding: 6px; border-bottom: 1px solid #e7e5e4; vertical-align: top; }
      .win { font-weight: 600; }
      .size { white-space: nowrap; font-family: monospace; font-size: 11px; }
      .fabric { max-width: 100px; word-break: break-word; }
      .qty { text-align: center; }
      .sub { font-size: 9px; color: #888; }
      .check-h, .check { text-align: center; width: 36px; }
      .box { display: inline-block; width: 16px; height: 16px; border: 2px solid #555; border-radius: 2px; vertical-align: middle; }
      .notes-h, .notes { width: 100px; }
      .notes { border-bottom: 1px dotted #bbb; }
      .footer { margin-top: 20px; border-top: 1px solid #ddd; padding-top: 8px; display: flex; justify-content: space-between; font-size: 10px; color: #888; }
      .sign-block { margin-top: 30px; display: flex; gap: 40px; }
      .sign-line { flex: 1; border-bottom: 1px solid #555; padding-bottom: 4px; font-size: 10px; color: #888; }
      @media print { body { padding: 0; } }
    </style>
  </head><body>
    <div class="header">
      <div>
        <h1>APOLLO SHADES</h1>
        <div style="font-size:11px;color:#666;">Installer Room Sheet</div>
      </div>
      <div class="meta">
        <strong>${esc(docType)}:</strong> #${esc(ref)}<br>
        <strong>Customer:</strong> ${esc(customer)}<br>
        ${address ? `<strong>Address:</strong> ${esc(address)}<br>` : ''}
        <strong>Date:</strong> ${date}
      </div>
    </div>

    ${roomsHtml}

    <div class="sign-block">
      <div class="sign-line">Installer signature</div>
      <div class="sign-line">Date completed</div>
      <div class="sign-line">Customer signature (optional)</div>
    </div>

    <div class="footer">
      <span>Apollo Shades — 1 Brooklyn Navy Yard, Building 33, Suite 201B, Brooklyn, NY 11205 — (212) 209-0654</span>
      <span>Printed ${new Date().toLocaleDateString()}</span>
    </div>
  </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
};
